import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MotionButton, MotionCard, FadeInSection, MotionAnchor } from '../ui/MotionPrimitives';

interface Listing {
  id: number;
  priceLabel: string;
  price?: string;
  title: string;
  beds: string;
  baths: string;
  area: string;
  image: string;
  location?: string;
  type?: string;
  description?: string;
  yearBuilt?: number;
  parking?: string;
  amenities?: string[];
  imageLocation?: string;
  imageBedrooms?: string;
  imageMore?: string;
}

// Extract file ID from Google Drive URL
const extractDriveFileId = (url: string): string | null => {
  if (!url || typeof url !== 'string') return null;
  
  try {
    // Clean URL and extract from iframe if present
    let cleanUrl = url.trim().replace(/\s+/g, '');
    
    // Handle iframe embed codes
    const iframeMatch = cleanUrl.match(/src=["']([^"']+)["']/);
    if (iframeMatch && iframeMatch[1]) {
      cleanUrl = iframeMatch[1];
    }
    
    // Pattern 1: /file/d/FILE_ID/view, /file/d/FILE_ID/preview, or /file/d/FILE_ID/edit
    let match = cleanUrl.match(/\/file\/d\/([a-zA-Z0-9_-]{25,})/);
    if (match && match[1]) {
      return match[1];
    }
    
    // Pattern 2: ?id=FILE_ID
    match = cleanUrl.match(/[?&]id=([a-zA-Z0-9_-]{25,})/);
    if (match && match[1]) {
      return match[1];
    }
    
    // Pattern 3: /d/FILE_ID
    match = cleanUrl.match(/\/d\/([a-zA-Z0-9_-]{25,})/);
    if (match && match[1]) {
      return match[1];
    }
    
    // Fallback: Parse URL
    const parsed = new URL(cleanUrl);
    if (parsed.pathname.includes('/file/d/')) {
      const parts = parsed.pathname.split('/');
      const dIndex = parts.indexOf('d');
      if (dIndex !== -1 && parts[dIndex + 1]) {
        return parts[dIndex + 1];
      }
    }
    
    return parsed.searchParams.get('id');
  } catch (error) {
    console.warn('Error extracting Drive file ID from URL:', url, error);
    return null;
  }
};

const formatImageUrl = (rawUrl?: string) => {
  if (!rawUrl) {
    console.warn('formatImageUrl: No URL provided');
    return '';
  }
  
  let url = String(rawUrl).trim();
  
  // Check if it's an iframe embed code and extract the src URL
  if (url.includes('<iframe') && url.includes('src=')) {
    const iframeMatch = url.match(/src=["']([^"']*drive\.google\.com[^"']*)["']/i);
    if (iframeMatch && iframeMatch[1]) {
      url = iframeMatch[1];
      console.log('📦 Extracted URL from iframe embed code');
    }
  }
  
  // Clean whitespace and newlines
  url = url.replace(/\s+/g, '').replace(/\n/g, '');
  
  if (!url) {
    console.warn('formatImageUrl: Empty URL after cleaning');
    return '';
  }

  if (url.includes('drive.google.com')) {
    // Check if URL is already a thumbnail or uc?export URL - use it directly
    if (url.includes('/thumbnail?id=') || url.includes('/uc?export=')) {
      console.log('✅ Google Drive URL already in correct format, using directly');
      return url;
    }
    
    const fileId = extractDriveFileId(url);
    if (fileId) {
      // Use thumbnail endpoint - most reliable for images when files are shared
      const imageUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
      console.log('✅ Converted Google Drive link to thumbnail URL:', { 
        original: url.substring(0, 100), 
        fileId, 
        imageUrl
      });
      return imageUrl;
    } else {
      console.warn('formatImageUrl: Could not extract file ID from URL:', url.substring(0, 100));
      // Return original URL if we can't extract ID - might work for some formats
      return url;
    }
  }

  // Return original URL if not a Google Drive URL
  return url.startsWith('http') ? url : `${window.location.origin}${url}`;
};

// Get alternative URLs for error fallback
const getAlternativeImageUrl = (rawUrl?: string): string[] => {
  if (!rawUrl) return [];
  const url = rawUrl.trim();
  if (!url.includes('drive.google.com')) return [];
  
  const fileId = extractDriveFileId(url);
  if (!fileId) {
    console.warn('getAlternativeImageUrl: Could not extract file ID');
    return [];
  }
  
  return [
    // uc?export=view - try this first as fallback
    `https://drive.google.com/uc?export=view&id=${fileId}`,
    // Larger thumbnail sizes
    `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`,
    `https://drive.google.com/thumbnail?id=${fileId}&sz=w4000`,
    // Download URL (last resort)
    `https://drive.google.com/uc?export=download&id=${fileId}`,
  ];
};

const getCellValue = (row: any, target: string) => {
  if (!row || typeof row !== 'object') return undefined;
  const key = Object.keys(row).find((k) => k.trim().toLowerCase() === target);
  return key ? row[key] : undefined;
};

// Helper component for images with fallback URLs
const ImageWithFallback: React.FC<{
  src: string;
  alt: string;
  className?: string;
  fallbackUrls?: string[];
}> = ({ src, alt, className, fallbackUrls = [] }) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [errorCount, setErrorCount] = useState(0);

  const handleError = () => {
    if (errorCount < fallbackUrls.length) {
      setCurrentSrc(fallbackUrls[errorCount]);
      setErrorCount(errorCount + 1);
    }
  };

  // Reset when src changes
  useEffect(() => {
    setCurrentSrc(src);
    setErrorCount(0);
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={(e) => {
        console.warn(`Image failed to load: ${currentSrc}`, {
          errorCount,
          fallbackUrls,
          hasMoreFallbacks: errorCount < fallbackUrls.length
        });
        handleError();
      }}
      onLoad={() => {
        if (process.env.NODE_ENV === 'development' && currentSrc !== src) {
          console.log(`Image loaded successfully with fallback: ${currentSrc}`);
        }
      }}
      loading="lazy"
    />
  );
};

const FeaturedListings: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch(
          'https://script.google.com/macros/s/AKfycbyvxxFdNiHzmY1hV-ViBjkDAZ8KKE_M8pnKssxQVfY2FJYBZH1wLSW0rHvsMw0dv0Dn5A/exec?sheet=All',
        );
        if (!res.ok) return;

        const rows = await res.json();
        if (!Array.isArray(rows)) return;

        const mapped: Listing[] = rows
          .map((row: any): Listing | null => {
            if (!row) return null;

            const imageValue = getCellValue(row, 'image');
            const imageLocationValue = getCellValue(row, 'imagelocation');
            const imageBedroomsValue = getCellValue(row, 'imagebedrooms');
            const imageMoreValue = getCellValue(row, 'imagemore');

            // amenities can be array or comma-separated string
            const amenitiesFromSheet: string[] | undefined = Array.isArray(row.amenities)
              ? (row.amenities as string[])
              : typeof row.amenities === 'string' && row.amenities
              ? String(row.amenities)
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : undefined;

            return {
              id: Number(row.id || 0),
              priceLabel: String(row.priceLabel || ''),
              price: row.price ? String(row.price) : undefined,
              title: String(row.title || ''),
              beds: String(row.beds || ''),
              baths: String(row.baths || ''),
              area: String(row.area || ''),
              image: imageValue ? String(imageValue) : '',
              location: row.location ? String(row.location) : undefined,
              type: row.type ? String(row.type) : undefined,
              description: row.description ? String(row.description) : undefined,
              yearBuilt: row.yearBuilt ? Number(row.yearBuilt) : undefined,
              parking: row.parking ? String(row.parking) : undefined,
              amenities: amenitiesFromSheet,
              imageLocation: imageLocationValue ? String(imageLocationValue) : undefined,
              imageBedrooms: imageBedroomsValue ? String(imageBedroomsValue) : undefined,
              imageMore: imageMoreValue ? String(imageMoreValue) : undefined,
            };
          })
          .filter((l): l is Listing => l !== null);

        setListings(mapped);
      } catch {
        setListings([]);
      }
    };

    fetchFeatured();
  }, []);

  const featuredListing = listings[0];
  const otherListings = listings.slice(1, 5); // next few for the grid

  return (
    <FadeInSection className="w-full bg-white py-14 md:py-18 lg:py-20">
      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 md:px-10">
        {/* Heading row */}
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-black md:text-3xl">
              Discover Our Featured Listings
            </h2>
            <p className="mt-1 text-sm text-black/60 md:text-base">
              A curated selection of premium homes and investments from our global portfolio.
            </p>
          </div>

          <MotionButton
            type="button"
            onClick={() => navigate('/properties')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#D88546] transition-colors hover:text-[#e59252]"
          >
            <span>See All Properties</span>
            <span className="text-lg">↗</span>
          </MotionButton>
        </div>

        {/* Main content area */}
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Featured property (left side) */}
          <div className="w-full lg:w-1/2">
            {featuredListing && (
              <MotionCard
                className="relative overflow-hidden rounded-2xl bg-white shadow-md cursor-pointer"
                onClick={() => setSelectedListing(featuredListing)}
              >
                <div className="relative h-[335px] w-full md:h-[399px]">
                  <ImageWithFallback
                    src={formatImageUrl(featuredListing.image)}
                    alt={featuredListing.title}
                    className="h-full w-full object-cover"
                    fallbackUrls={getAlternativeImageUrl(featuredListing.image)}
                  />
                  <div className="absolute left-4 top-4 rounded-full bg-[#D88546] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    Featured
                  </div>
                  {/* Text overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                    <div className="text-2xl font-bold">{featuredListing.price || featuredListing.priceLabel}</div>
                    <h3 className="text-lg font-semibold">{featuredListing.title}</h3>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <span>{featuredListing.beds}</span>
                      <span className="h-1 w-1 rounded-full bg-white/70"></span>
                      <span>{featuredListing.baths}</span>
                      <span className="h-1 w-1 rounded-full bg-white/70"></span>
                      <span>{featuredListing.area}</span>
                    </div>
                  </div>
                </div>
              </MotionCard>
            )}
          </div>

          {/* Grid of properties (right side) */}
          <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-2 lg:w-1/2 lg:grid-cols-2">
            {otherListings.map((listing) => (
              <MotionCard
                key={listing.id}
                className="relative overflow-hidden rounded-2xl bg-white shadow-md cursor-pointer"
                onClick={() => setSelectedListing(listing)}
              >
                <div className="relative h-48 w-full">
                  <ImageWithFallback
                    src={formatImageUrl(listing.image)}
                    alt={listing.title}
                    className="h-full w-full object-cover"
                    fallbackUrls={getAlternativeImageUrl(listing.image)}
                  />
                  {/* Text overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                    <div className="text-lg font-bold">{listing.price || listing.priceLabel}</div>
                    <h3 className="text-sm font-semibold">{listing.title}</h3>
                    <div className="mt-0.5 flex items-center gap-1 text-[10px]">
                      <span>{listing.beds}</span>
                      <span className="h-0.5 w-0.5 rounded-full bg-white/70"></span>
                      <span>{listing.baths}</span>
                      <span className="h-0.5 w-0.5 rounded-full bg-white/70"></span>
                      <span>{listing.area}</span>
                    </div>
                  </div>
                </div>
              </MotionCard>
            ))}
          </div>
        </div>

        {/* Modal for featured listing details (Home page) */}
        {selectedListing && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSelectedListing(null)}
          >
            <div
              className="flex w-full max-w-3xl max-h-[90vh] flex-col rounded-2xl bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex flex-shrink-0 items-start justify-between p-6 md:p-8">
                <div>
                  {selectedListing.type && (
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#D88546]">
                      {selectedListing.type}
                    </p>
                  )}
                  <h2 className="mt-1 text-xl font-semibold text-black md:text-2xl">
                    {selectedListing.title}
                  </h2>
                  {selectedListing.location && (
                    <p className="mt-1 text-sm text-black/70">{selectedListing.location}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedListing(null)}
                  className="rounded-full bg-gray-100 p-2 text-black transition hover:bg-gray-200"
                >
                  <span className="block h-5 w-5 text-2xl leading-none">×</span>
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-6 pb-6 md:px-8">
                {/* Image row (main + extra) */}
                <div className="flex flex-col gap-4 pb-6 md:flex-row">
                  <div className="flex-1">
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                      Location View
                    </h3>
                    <div className="relative h-48 w-full overflow-hidden rounded-xl bg-gray-100">
                      <ImageWithFallback
                        src={formatImageUrl(selectedListing.imageLocation || selectedListing.image)}
                        alt="Location view"
                        className="h-full w-full object-cover"
                        fallbackUrls={getAlternativeImageUrl(selectedListing.imageLocation || selectedListing.image)}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                      Bedrooms
                    </h3>
                    <div className="relative h-48 w-full overflow-hidden rounded-xl bg-gray-100">
                      <ImageWithFallback
                        src={formatImageUrl(selectedListing.imageBedrooms || selectedListing.image)}
                        alt="Bedrooms"
                        className="h-full w-full object-cover"
                        fallbackUrls={getAlternativeImageUrl(selectedListing.imageBedrooms || selectedListing.image)}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                      More
                    </h3>
                    <div className="relative h-48 w-full overflow-hidden rounded-xl bg-gray-100">
                      <ImageWithFallback
                        src={formatImageUrl(selectedListing.imageMore || selectedListing.image)}
                        alt="More details"
                        className="h-full w-full object-cover"
                        fallbackUrls={getAlternativeImageUrl(selectedListing.imageMore || selectedListing.image)}
                      />
                    </div>
                  </div>
                </div>

                {/* Details and price */}
                <div className="flex flex-col gap-4 pb-6 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-4 text-sm text-black/70">
                    <span>{selectedListing.beds}</span>
                    <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                    <span>{selectedListing.baths}</span>
                    <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                    <span>{selectedListing.area}</span>
                    {selectedListing.yearBuilt && (
                      <>
                        <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                        <span>Built: {selectedListing.yearBuilt}</span>
                      </>
                    )}
                    {selectedListing.parking && (
                      <>
                        <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                        <span>{selectedListing.parking}</span>
                      </>
                    )}
                  </div>
                  <div className="text-lg font-bold text-black md:text-xl">
                    {selectedListing.price || selectedListing.priceLabel}
                  </div>
                </div>

                {/* Description */}
                {selectedListing.description && (
                  <div className="border-t pb-6 pt-4">
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                      Description
                    </h3>
                    <p className="text-sm leading-relaxed text-black/80">
                      {selectedListing.description}
                    </p>
                  </div>
                )}

                {/* Amenities */}
                {selectedListing.amenities && selectedListing.amenities.length > 0 && (
                  <div className="border-t pb-6 pt-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                      Amenities
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedListing.amenities.map((amenity, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-black"
                        >
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Fixed footer CTA */}
              <div className="flex-shrink-0 border-t p-6 md:px-8">
                <MotionAnchor
                  href={`https://wa.me/971508401805?text=${encodeURIComponent(
                    `Hi, I'm interested in this featured property:\n\n` +
                    `Title: ${selectedListing.title}\n` +
                    (selectedListing.type ? `Type: ${selectedListing.type}\n` : '') +
                    (selectedListing.location ? `Location: ${selectedListing.location}\n` : '') +
                    `Price: ${selectedListing.price || selectedListing.priceLabel}\n` +
                    `Details: ${selectedListing.beds} · ${selectedListing.baths} · ${selectedListing.area}\n` +
                    (selectedListing.yearBuilt ? `Year Built: ${selectedListing.yearBuilt}\n` : '') +
                    (selectedListing.parking ? `Parking: ${selectedListing.parking}\n` : '') +
                    (selectedListing.description ? `Description: ${selectedListing.description}\n` : '') +
                    (selectedListing.amenities && selectedListing.amenities.length
                      ? `Amenities: ${selectedListing.amenities.join(', ')}\n`
                      : '') +
                    `\nImages:\n` +
                    `• Main: ${formatImageUrl(selectedListing.image)}\n` +
                    `• Location: ${formatImageUrl(selectedListing.imageLocation || selectedListing.image)}\n` +
                    `• Bedrooms: ${formatImageUrl(selectedListing.imageBedrooms || selectedListing.image)}\n` +
                    `• More: ${formatImageUrl(selectedListing.imageMore || selectedListing.image)}\n\n` +
                    `Could you provide more details?`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#D88546] px-6 py-3 text-sm font-semibold text-black transition hover:bg-[#e59252] md:w-auto"
                >
                  Send Inquiry
                </MotionAnchor>
              </div>
            </div>
          </div>
        )}
      </div>
    </FadeInSection>
  );
};

export default FeaturedListings;
